import { useEffect, useRef, useState } from 'react';
import { RefreshCw, X } from 'lucide-react';
import { changesSince, mayBeIncomplete, type ReleaseChange } from '@shared/release-notes';
import { Button } from '@/components/ui/button';
import { WhatsNewDialog } from '@/components/WhatsNewDialog';
import {
  APP_BUILD_ID,
  APP_BUILT_FROM_AT,
  APP_UPDATE_POLL_MS,
  fetchReleaseNotes,
  fetchRemoteBuildId,
  isNewerBuild,
  reloadApp,
} from '@/lib/app-update';
import { listProjects } from '@/lib/projects-api';
import { projectPath } from '@/navigation/project-paths';
import { useOnlineStatus } from '@/hooks/use-online-status';
import { useAuthStore } from '@/stores/authStore';

type WhatsNew = {
  changes: ReleaseChange[] | null;
  repo: string | null;
  /** The factory's own project (the one whose repo the notes are from), for task links; null if not one of yours. */
  slug: string | null;
  /** Every commit between this app and the deployed one, on GitHub; null when either build is unknown. */
  compareHref: string | null;
  /** The list may not reach back to this app's version (a shallow build history). */
  incomplete: boolean;
};

const LOADING: WhatsNew = { changes: null, repo: null, slug: null, compareHref: null, incomplete: false };

/** The notes, the changes newer than this app, and the project on this repo you can open, if any. */
async function loadWhatsNew(): Promise<WhatsNew> {
  // Signed out (the login page), there are no projects to link to.
  const signedIn = Boolean(useAuthStore.getState().user);
  const [notes, projects] = await Promise.all([fetchReleaseNotes(), signedIn ? listProjects() : null]);
  if (!notes) return { ...LOADING, changes: [] };
  const repo = notes.repo.toLowerCase();
  const project = projects?.data?.projects.find((p) => p.repo_full_name === repo && !p.archived_at);
  const known = APP_BUILD_ID !== 'dev' && notes.buildId && notes.buildId !== APP_BUILD_ID;
  return {
    changes: changesSince(notes.changes, APP_BUILT_FROM_AT),
    repo: notes.repo,
    slug: project?.slug ?? null,
    compareHref: known ? `https://github.com/${notes.repo}/compare/${APP_BUILD_ID}...${notes.buildId}` : null,
    incomplete: mayBeIncomplete(notes.changes, APP_BUILT_FROM_AT),
  };
}

/**
 * Fixed banner when a newer deploy is available. Reload only on explicit Update
 * so work in progress is not interrupted. What's new lists what the update brings.
 */
export function AppUpdateBanner() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [dismissedBuildId, setDismissedBuildId] = useState<string | null>(null);
  const remoteBuildIdRef = useRef<string | null>(null);
  // Offline, the offline notice takes this slot; an update can wait until the connection is back.
  const online = useOnlineStatus();
  // "What's new" (T-16): loaded when opened, so the five-minute check stays one small request.
  const [whatsNew, setWhatsNew] = useState<WhatsNew | null>(null);
  const openWhatsNew = () => {
    setWhatsNew(LOADING);
    void loadWhatsNew().then((loaded) => setWhatsNew((current) => (current ? loaded : current)));
  };

  useEffect(() => {
    const checkForUpdate = () =>
      fetchRemoteBuildId().then((remote) => {
        remoteBuildIdRef.current = remote;
        // Stay dismissed for this build during the session; a different newer build shows again.
        setUpdateAvailable(isNewerBuild(remote) && remote !== dismissedBuildId);
      });

    void checkForUpdate();

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        void checkForUpdate();
      }
    };
    const onPageShow = () => {
      void checkForUpdate();
    };

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pageshow', onPageShow);

    const intervalId = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        void checkForUpdate();
      }
    }, APP_UPDATE_POLL_MS);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pageshow', onPageShow);
      window.clearInterval(intervalId);
    };
  }, [dismissedBuildId]);

  if (!updateAvailable || !online) return null;

  // The dialog has its own Update now; the banner would sit over its top on a phone.
  if (whatsNew) {
    const { slug } = whatsNew;
    return (
      <WhatsNewDialog
        open
        onClose={() => setWhatsNew(null)}
        changes={whatsNew.changes}
        repo={whatsNew.repo}
        compareHref={whatsNew.compareHref}
        incomplete={whatsNew.incomplete}
        taskHref={(ref) => (slug ? `${projectPath(slug)}?q=${encodeURIComponent(ref)}` : null)}
        onUpdate={() => reloadApp()}
      />
    );
  }

  // An open modal (a task, a dialog) sets pointer-events: none on the body, so taps would fall through
  // to it: pointer-events-auto keeps the banner pressable, and stopping pointerdown here keeps the
  // modal from treating a tap on the banner as a tap outside and closing.
  return (
    <div
      role="status"
      aria-live="polite"
      onPointerDown={(event) => event.stopPropagation()}
      className="pointer-events-auto fixed inset-x-0 top-0 z-[10001] flex items-center justify-center gap-3 border-b border-primary/20 bg-primary px-3 py-2 text-primary-foreground shadow-sm"
    >
      <p className="min-w-0 flex-1 text-center text-sm font-medium sm:flex-none">
        A new version is available
      </p>
      <div className="flex shrink-0 items-center gap-1.5">
        <Button
          size="compact"
          variant="ghost"
          className="text-primary-foreground underline-offset-4 hover:bg-primary-foreground/15 hover:text-primary-foreground hover:underline"
          onClick={openWhatsNew}
        >
          What's new
        </Button>
        <Button
          size="compact"
          variant="secondary"
          className="gap-1.5 bg-primary-foreground text-primary hover:bg-primary-foreground/90"
          onClick={() => reloadApp()}
        >
          <RefreshCw className="size-3.5" />
          Update
        </Button>
        <Button
          size="icon-compact"
          variant="ghost"
          className="text-primary-foreground hover:bg-primary-foreground/15 hover:text-primary-foreground"
          aria-label="Dismiss update banner"
          onClick={() => {
            setDismissedBuildId(remoteBuildIdRef.current);
            setUpdateAvailable(false);
          }}
        >
          <X className="size-3.5" />
        </Button>
      </div>
    </div>
  );
}
